import {
  AnyDataPayload,
  convertUnfurlDataToUnfurlItem,
  getCollectionNameFromUnfurlData,
  getCollectionTypeFromUnfurlData,
  isGoogleDriveFolderData,
  isYoutubeChannel,
  isYoutubeMultipleData,
  UnfurlItem,
} from '@lib/external/unfurl';
import { FOLDER_ROOT_PATH, type FolderPath } from '@lib/library/folderPath';
import { createContext } from '@lib/mobx/context';
import { ReactionManager } from '@lib/mobx/reaction-manager';
import {
  filterOutDuplicates,
  generateHashesForItems,
  isMediaDuplicate,
} from '@lib/utils/chooseMediaUtils';
import { estimateIngest, type IngestEstimate } from '@lib/utils/ingestEstimate';
import { DEFAULT_LIBRARY_NAME } from '@pages/Libraries/components/LibraryCreateModal';
import { AddMediaValidationService } from '@services/AddMediaValidationService';
import { unfurlCache } from '@services/unfurlCache';
import { URLService } from '@services/URLService';
import { trackingStore } from '@stores/Tracking';
import { UploadURLResult } from '@stores/UploadManager/types';
import { UploadManager } from '@stores/UploadManager/UploadManager';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { ERROR_DURATION_THRESHOLD, MediaStatusType } from './mediaStatus';

export type URLStatus = 'loading' | 'error' | 'duplicate' | 'valid';
/** What the session scrape cache held for a URL — see `applyCachedRecord`. */
type CacheState = 'hydrated' | 'live' | 'miss';
export type OverallURLStatus = 'empty' | 'loading' | 'partial' | 'allValid' | 'allError';
export const DEFAULT_COLLECTION_NAME = 'Unnamed Media Collection';

export type URLEntry = {
  id: string;
  url: string;
  status: URLStatus;
  errorMessage: string;
  /**
   * Raw unfurl data from the API endpoint.
   *
   * Terminology distinction:
   * - `unfurlData`: Raw AnyDataPayload from the unfurl API endpoint
   * - `enhancedVideos`: Processed UnfurlItem[] extracted from unfurlData and enhanced with uniqueIds
   *
   * Use convertUnfurlDataToUnfurlItem() to convert unfurlData to processed items.
   */
  unfurlData: AnyDataPayload | null;
  /**
   * Processed and enhanced media items.
   * Extracted from unfurlData via convertUnfurlDataToUnfurlItem() and enhanced with uniqueIds.
   */
  enhancedVideos: UnfurlItem[];
  isEnhancing: boolean;
  /**
   * Detaches this entry from the session scrape cache.
   *
   * Note this is NOT a canceller: calling it stops the entry listening, but the scrape keeps
   * running in `unfurlCache` so that closing the modal — or briefly mistyping the URL — can't
   * destroy work that took minutes. See `UnfurlScrapeCache`.
   */
  unsubscribeFromCache: (() => void) | null;
  debounceTimeout: NodeJS.Timeout | null;
  /**
   * Loading progress during SSE streaming.
   * Tracks how many entities have been loaded so far.
   */
  loadingProgress: {
    loaded: number;
    isStreaming: boolean;
  };
};

export class UploadURLStore extends ReactionManager {
  // New multi-URL fields
  @observable rawInput: string = '';
  @observable urlEntries: URLEntry[] = [];

  // Legacy single-URL fields (kept for backwards compatibility during migration)
  @observable isAddingUrl: boolean = false;
  @observable libraryName: string | undefined = undefined;
  @observable userIntent: 'single' | 'playlist' | null = null;
  @observable collectionName: string = DEFAULT_COLLECTION_NAME;

  // Status filter state (Phase 3)
  @observable activeStatusFilters: Set<MediaStatusType> = new Set();
  @observable statusDismissed: boolean = false;

  /**
   * Whether the media picker has already been auto-opened for the current input.
   *
   * This lives on the store rather than in `UrlImportComponent` because that component sits
   * inside a keyed `LibraryFolderProvider` and so remounts when the destination library changes,
   * while the entries the latch guards now survive in this store. As component state the latch
   * reset on that remount, and the auto-open effect promptly reopened the picker unprompted.
   */
  @observable pickerAutoOpened: boolean = false;

  private urlService: URLService;
  public readonly validationService: AddMediaValidationService;
  private readonly debounceDelay: number = 800;

  constructor() {
    super();
    makeObservable(this);
    this.urlService = URLService.getInstance();
    this.validationService = new AddMediaValidationService();

    // Re-arm the auto-open latch whenever the input actually changes, so a new or edited URL can
    // open the picker again. Deliberately a reaction and not a component effect: it must fire on
    // a real change to the input, never merely because a component remounted.
    this.reaction(
      () => this.rawInput,
      () => this.setPickerAutoOpened(false),
    );
  }

  @action
  setPickerAutoOpened(value: boolean) {
    this.pickerAutoOpened = value;
  }

  // ============================================
  // Multi-URL Input Handling
  // ============================================

  @action
  setRawInput(input: string) {
    this.rawInput = input;

    // Parse URLs from whitespace-separated input
    const urls = input
      .split(/\s+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    // Deduplicate URLs (keep first occurrence)
    const uniqueUrls = [...new Set(urls)];

    this.syncUrlEntries(uniqueUrls);
  }

  @action
  setValidatedEntries(entries: URLEntry[]) {
    // Update rawInput to match the entries
    this.rawInput = entries.map((e) => e.url).join('\n');

    // Detach existing entries. Deliberately does not cancel the scrapes behind them — the
    // rebuilt entries below re-attach to the same in-flight work via the cache.
    this.urlEntries.forEach((e) => {
      e.unsubscribeFromCache?.();
      if (e.debounceTimeout) {
        clearTimeout(e.debounceTimeout);
      }
    });

    // Map existing entries by URL to preserve enhancedVideos if they exist
    const existingByUrl = new Map(this.urlEntries.map((e) => [e.url, e]));

    // Replace entries with validated ones, preserving enhanced videos from existing entries
    this.urlEntries = entries.map((entry) => {
      const existing = existingByUrl.get(entry.url);

      // If we have an existing entry with enhanced videos, preserve them
      if (existing && existing.enhancedVideos.length > 0) {
        return {
          ...entry,
          enhancedVideos: existing.enhancedVideos,
          isEnhancing: false,
          // Ensure cache subscriptions and timeouts are reset
          unsubscribeFromCache: null,
          debounceTimeout: null,
          // Ensure loadingProgress exists
          loadingProgress: entry.loadingProgress || {
            loaded: 0,
            isStreaming: false,
          },
        };
      }

      // Otherwise use the entry as-is (may need enhancement)
      return {
        ...entry,
        // Ensure cache subscriptions and timeouts are reset
        unsubscribeFromCache: null,
        debounceTimeout: null,
        // Ensure loadingProgress exists
        loadingProgress: entry.loadingProgress || {
          loaded: 0,
          isStreaming: false,
        },
      };
    });

    // Only trigger validation/enhancement for entries that need it
    this.urlEntries.forEach((entry) => {
      if (entry.status === 'loading' && entry.unfurlData === null) {
        // New entry that needs validation
        this.debounceFetchForEntry(entry.id);
      } else if (
        entry.status === 'valid' &&
        entry.unfurlData &&
        entry.enhancedVideos.length === 0 &&
        !entry.isEnhancing
      ) {
        // Valid entry with unfurlData but no enhanced videos - enhance them
        const videos = convertUnfurlDataToUnfurlItem(entry.unfurlData, entry.url);
        if (process.env.NODE_ENV === 'development') {
          console.log('setValidatedEntries: Triggering enhancement for entry', {
            entryId: entry.id,
            url: entry.url,
            unfurlData: entry.unfurlData,
            videosLength: videos.length,
          });
        }
        this.enhanceEntryVideos(entry.id);
      }
    });

    // Update collection name based on first valid entry
    this.updateCollectionNameFromEntries();
  }

  @action
  private syncUrlEntries(urls: string[]) {
    const existingByUrl = new Map(this.urlEntries.map((e) => [e.url, e]));
    const newUrlSet = new Set(urls);

    // Detach removed URLs and clear their debounces. The scrape itself is left running: the
    // user may be mid-typo, and re-entering the URL should re-attach rather than restart.
    this.urlEntries
      .filter((e) => !newUrlSet.has(e.url))
      .forEach((e) => {
        e.unsubscribeFromCache?.();
        if (e.debounceTimeout) {
          clearTimeout(e.debounceTimeout);
        }
      });

    // Brand-new entries and what the cache had for each. Collected here because the work below
    // has to wait until `this.urlEntries` is assigned — `enhanceEntryVideos` and
    // `fetchUnfurlDataForEntry` both look entries up by id.
    const pendingWork: Array<{ entryId: string; cacheState: CacheState }> = [];

    const newEntries: URLEntry[] = urls.map((url) => {
      const existing = existingByUrl.get(url);
      if (existing) {
        return existing;
      }

      // Create new entry
      const entry: URLEntry = {
        id: crypto.randomUUID(),
        url,
        status: 'loading',
        errorMessage: '',
        unfurlData: null,
        enhancedVideos: [],
        isEnhancing: false,
        unsubscribeFromCache: null,
        debounceTimeout: null,
        loadingProgress: {
          loaded: 0,
          isStreaming: false,
        },
      };

      // Populate from the session cache before the entry is ever rendered, so a URL already
      // scraped this session reads as complete on the first frame instead of flashing a spinner.
      pendingWork.push({ entryId: entry.id, cacheState: this.applyCachedRecord(entry) });

      return entry;
    });

    this.urlEntries = newEntries;

    pendingWork.forEach(({ entryId, cacheState }) => this.startWorkForEntry(entryId, cacheState));

    // Update collection name based on first valid entry
    this.updateCollectionNameFromEntries();
  }

  /**
   * Kicks off whatever a freshly-seeded entry still needs, given what the cache had for it.
   * Must run after the entry is in `this.urlEntries` — every branch looks it up by id.
   */
  @action
  private startWorkForEntry(entryId: string, cacheState: CacheState) {
    switch (cacheState) {
      case 'hydrated':
        // Finished scrape already in hand — skip both the debounce and the fetch.
        this.enhanceEntryVideos(entryId);
        this.updateCollectionNameFromEntries();
        break;
      case 'live':
        // Attach immediately: joining a running scrape costs nothing, so there is nothing to
        // debounce, and waiting would only leave the counter looking frozen.
        this.fetchUnfurlDataForEntry(entryId);
        break;
      default:
        this.debounceFetchForEntry(entryId);
    }
  }

  /**
   * Seeds an entry from the session scrape cache.
   *
   * - `'hydrated'` — a finished scrape was found and the entry is fully populated.
   * - `'live'` — a scrape is still running. The entry adopts its current count so a reopened
   *   modal resumes the number instead of restarting at zero.
   * - `'miss'` — nothing cached.
   *
   * Pass the result to `startWorkForEntry` once the entry is in `this.urlEntries`.
   */
  @action
  private applyCachedRecord(entry: URLEntry): CacheState {
    const cached = unfurlCache.getRecord(entry.url);
    if (!cached) {
      entry.loadingProgress = { loaded: 0, isStreaming: false };
      return 'miss';
    }

    if (cached.isComplete && cached.result?.valid && cached.result.unfurlData) {
      entry.status = 'valid';
      entry.errorMessage = '';
      entry.unfurlData = cached.result.unfurlData;
      entry.loadingProgress = { loaded: cached.loadedCount, isStreaming: false };
      return 'hydrated';
    }

    entry.loadingProgress = { loaded: cached.loadedCount, isStreaming: true };
    return 'live';
  }

  @action
  private debounceFetchForEntry(entryId: string) {
    const entry = this.urlEntries.find((e) => e.id === entryId);
    if (!entry) return;

    // Clear existing debounce
    if (entry.debounceTimeout) {
      clearTimeout(entry.debounceTimeout);
    }

    entry.debounceTimeout = setTimeout(() => {
      this.fetchUnfurlDataForEntry(entryId);
    }, this.debounceDelay);
  }

  /**
   * Resolves an entry's URL through the session scrape cache.
   *
   * The cache — not this store — owns the request, so this method starts or joins a scrape but
   * never cancels one. That's what lets an in-flight channel scrape survive the modal closing.
   */
  @action
  async fetchUnfurlDataForEntry(entryId: string) {
    const entry = this.urlEntries.find((e) => e.id === entryId);
    if (!entry) return;

    // Clear debounce timeout
    if (entry.debounceTimeout) {
      clearTimeout(entry.debounceTimeout);
      entry.debounceTimeout = null;
    }

    // Detach from a previous subscription without cancelling the scrape behind it.
    entry.unsubscribeFromCache?.();
    entry.unsubscribeFromCache = null;

    // Pin the URL for this attempt: the entry may be re-pointed while the scrape runs, and any
    // result or progress arriving afterwards belongs to a URL we no longer care about.
    const url = entry.url;

    runInAction(() => {
      entry.status = 'loading';
      entry.errorMessage = '';
      // Adopt whatever the cache already knows, so re-attaching to a running scrape resumes
      // the count rather than restarting it at zero.
      const cached = unfurlCache.getRecord(url);
      entry.loadingProgress = {
        loaded: cached?.loadedCount ?? 0,
        isStreaming: true,
      };
    });

    const unsubscribe = unfurlCache.subscribe(url, (record) => {
      if (entry.url !== url) return;
      runInAction(() => {
        entry.loadingProgress = {
          loaded: record.loadedCount,
          isStreaming: !record.isComplete,
        };
      });
    });
    entry.unsubscribeFromCache = unsubscribe;

    try {
      const validationResult = await unfurlCache.start(url);

      // Re-find rather than reuse the captured entry: it may have been removed or replaced
      // while the scrape ran, and mutating a detached object would silently do nothing.
      const current = this.urlEntries.find((e) => e.id === entryId);
      if (!current || current.url !== url) return;

      if (validationResult.valid && validationResult.unfurlData) {
        runInAction(() => {
          current.unfurlData = validationResult.unfurlData!;
          current.status = 'valid';
          current.loadingProgress = {
            loaded: current.loadingProgress.loaded,
            isStreaming: false,
          };
          this.enhanceEntryVideos(entryId);
          this.updateCollectionNameFromEntries();
        });
      } else {
        this.failEntry(entryId, url, validationResult.reason);
      }
    } catch (error) {
      // Note `unfurlCache.start()` resolves rather than rejects on a failed scrape, so this
      // covers a throw from the success branch above, not the network path.
      this.failEntry(entryId, url, error instanceof Error ? error.message : undefined);
    } finally {
      // The scrape has settled, so no further progress is coming. Release this subscription
      // rather than leaving the closure registered against the URL for the rest of the session.
      unsubscribe();
      // Identity check: a re-fetch or a URL edit may already have installed a newer
      // subscription on this entry, and that one must not be dropped.
      if (entry.unsubscribeFromCache === unsubscribe) {
        runInAction(() => {
          entry.unsubscribeFromCache = null;
        });
      }
    }
  }

  /**
   * Marks an entry as failed. Re-finds it by id and re-checks the URL, so an entry that was
   * removed or re-pointed while the scrape ran is left alone.
   */
  @action
  private failEntry(entryId: string, url: string, reason?: string) {
    const current = this.urlEntries.find((e) => e.id === entryId);
    if (!current || current.url !== url) return;

    runInAction(() => {
      current.status = 'error';
      current.errorMessage = reason || 'Please enter a valid URL to upload from.';
      current.loadingProgress = { loaded: 0, isStreaming: false };
    });
  }

  @action
  async enhanceEntryVideos(entryId: string) {
    const entry = this.urlEntries.find((e) => e.id === entryId);
    if (!entry || !entry.unfurlData) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('enhanceEntryVideos: Entry not found or missing unfurlData', { entryId });
      }
      return;
    }

    // Check and set isEnhancing flag atomically to prevent race conditions
    if (entry.isEnhancing) return;

    runInAction(() => {
      entry.isEnhancing = true;
    });

    // Extract videos from unfurl data (after setting flag to prevent concurrent execution)
    const videos = convertUnfurlDataToUnfurlItem(entry.unfurlData, entry.url);
    if (videos.length === 0) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('enhanceEntryVideos: No videos extracted from unfurl data', {
          entryId,
          url: entry.url,
        });
      }
      runInAction(() => {
        entry.enhancedVideos = [];
        entry.isEnhancing = false;
      });
      return;
    }

    try {
      // convertUnfurlDataToUnfurlItem already returns UnfurlItem[], no cast needed
      const enhanced = await generateHashesForItems(videos);
      runInAction(() => {
        entry.enhancedVideos = enhanced;
      });
    } catch (error) {
      console.error('Failed to enhance videos for entry:', entryId, error);
      runInAction(() => {
        entry.enhancedVideos = [];
      });
    } finally {
      runInAction(() => {
        entry.isEnhancing = false;
      });
    }
  }

  @action
  private updateCollectionNameFromEntries() {
    const firstValidEntry = this.urlEntries.find((e) => e.status === 'valid' && e.unfurlData);
    if (firstValidEntry?.unfurlData) {
      const name = getCollectionNameFromUnfurlData(
        firstValidEntry.unfurlData,
        DEFAULT_COLLECTION_NAME,
      );
      this.setCollectionName(name);
      this.setLibraryName(this.suggestedLibraryName);
    }
  }

  @action
  retryEntry(entryId: string) {
    this.fetchUnfurlDataForEntry(entryId);
  }

  @action
  removeEntry(entryId: string) {
    const entry = this.urlEntries.find((e) => e.id === entryId);
    if (entry) {
      // Detach only. The scrape keeps running so re-adding the URL is instant.
      entry.unsubscribeFromCache?.();
      if (entry.debounceTimeout) {
        clearTimeout(entry.debounceTimeout);
      }
    }

    this.urlEntries = this.urlEntries.filter((e) => e.id !== entryId);

    // Update raw input to match
    this.rawInput = this.urlEntries.map((e) => e.url).join('\n');
  }

  @action
  updateEntryUrl(entryId: string, newUrl: string) {
    const entry = this.urlEntries.find((e) => e.id === entryId);
    if (!entry) return;

    const trimmedUrl = newUrl.trim();
    if (trimmedUrl === entry.url) return; // No change

    // Detach from the old URL's scrape but let it run: this fires on every keystroke of an
    // edit, and abandoning a half-finished channel scrape over a typo is the bug being fixed.
    entry.unsubscribeFromCache?.();
    entry.unsubscribeFromCache = null;
    if (entry.debounceTimeout) {
      clearTimeout(entry.debounceTimeout);
      entry.debounceTimeout = null;
    }

    // Update the entry URL
    entry.url = trimmedUrl;
    entry.status = 'loading';
    entry.errorMessage = '';
    entry.unfurlData = null;
    entry.enhancedVideos = [];
    entry.isEnhancing = false;
    entry.loadingProgress = {
      loaded: 0,
      isStreaming: false,
    };

    // Update raw input to match
    this.rawInput = this.urlEntries.map((e) => e.url).join('\n');

    // A URL edited back to one already scraped this session resolves without waiting out the
    // debounce — this is what makes recovering from a typo feel instant.
    this.startWorkForEntry(entryId, this.applyCachedRecord(entry));
  }

  // ============================================
  // Computed Aggregations (Multi-URL)
  // ============================================

  @computed
  get validEntries(): URLEntry[] {
    return this.urlEntries.filter((e) => e.status === 'valid');
  }

  @computed
  get errorEntries(): URLEntry[] {
    return this.urlEntries.filter((e) => e.status === 'error');
  }

  @computed
  get loadingEntries(): URLEntry[] {
    return this.urlEntries.filter((e) => e.status === 'loading');
  }

  @computed
  get overallStatus(): OverallURLStatus {
    if (this.urlEntries.length === 0) return 'empty';

    const statuses = this.urlEntries.map((e) => e.status);
    if (statuses.some((s) => s === 'loading')) return 'loading';
    if (statuses.every((s) => s === 'valid')) return 'allValid';
    if (statuses.every((s) => s === 'error')) return 'allError';
    return 'partial';
  }

  @computed
  get allEnhancedVideos(): UnfurlItem[] {
    return this.urlEntries.filter((e) => e.status === 'valid').flatMap((e) => e.enhancedVideos);
  }

  /**
   * The deduplicated, filtered media list across all valid entries — the rows
   * the ChooseMediaModal grid displays.
   *
   * Being a MobX computed is load-bearing, not a convenience: the returned
   * array keeps the SAME reference until one of the observables read here
   * (entries, their unfurlData/enhancedVideos) actually changes. This must not
   * read anything library-folder related — when it lived in UrlImportComponent
   * it was rebuilt on every render, so unrelated store churn (e.g. media
   * processing ticking libraryFolder.uniqueIds, which the component subscribes
   * to for duplicate badges) minted new row identities and made the grid
   * redraw and lose scroll position mid-browse.
   */
  @computed
  get unfurledItems(): UnfurlItem[] {
    const validEntries = this.validEntries;
    if (validEntries.length === 0) {
      return [];
    }

    // Aggregate videos from all valid entries
    const allVideos: UnfurlItem[] = [];
    // Track seen videos by uniqueId (preferred) or url (fallback) to prevent duplicates
    const seenVideos = new Set<string>();

    for (const entry of validEntries) {
      // Use enhanced videos if available, otherwise extract from unfurlData
      const videosToUse =
        entry.enhancedVideos.length > 0
          ? entry.enhancedVideos
          : entry.unfurlData
            ? convertUnfurlDataToUnfurlItem(entry.unfurlData, entry.url)
            : [];

      // Google Drive folders legitimately contain images and audio, which
      // report duration 0 — so the live-video filter below (which drops
      // duration-0 YouTube scheduled-live videos) must NOT apply to them.
      const isFolder = entry.unfurlData ? isGoogleDriveFolderData(entry.unfurlData) : false;

      // Add videos with position info
      // For single videos, inject the original URL from entry.url if the video doesn't have one
      const entryVideos = videosToUse
        // Filter out (scheduled) live videos, who have duration of 0.
        // Allow videos with undefined duration to pass through. Skipped for
        // Drive folders, where duration-0 items (images/audio) are valid.
        .filter((video) => isFolder || video.duration === undefined || video.duration > 0)
        .map((video) => ({
          ...video,
          // Inject the entry URL for single videos that don't have a URL
          url: video.url || entry.url,
        }));

      // Deduplicate: only add videos we haven't seen before
      // uniqueId may not be computed yet (it's async, done later by generateHashesForItems),
      // so we fall back to id or url for this initial in-memory dedup pass.
      for (const video of entryVideos) {
        const dedupeKey = video.uniqueId || video.id || video.url;

        // If no dedupeKey, assume not duplicate and include the video
        if (!dedupeKey || !seenVideos.has(dedupeKey)) {
          if (dedupeKey) {
            seenVideos.add(dedupeKey);
          }
          // Set position based on current length of allVideos (after deduplication)
          allVideos.push({
            ...video,
            defaultPosition: allVideos.length,
          });
        }
      }
    }

    return allVideos;
  }

  @computed
  get totalVideoCount(): number {
    return this.urlEntries
      .filter((e) => e.status === 'valid')
      .reduce((sum, e) => {
        if (e.unfurlData) {
          const videos = convertUnfurlDataToUnfurlItem(e.unfurlData, e.url);
          return sum + videos.length;
        }
        return sum;
      }, 0);
  }

  @computed
  get ingestEstimateAllEntries(): IngestEstimate {
    return estimateIngest(
      this.urlEntries.flatMap((e) =>
        e.status === 'valid' && e.unfurlData
          ? convertUnfurlDataToUnfurlItem(e.unfurlData, e.url)
          : [],
      ),
    );
  }

  @computed
  get isAnyEnhancing(): boolean {
    return this.urlEntries.some((e) => e.isEnhancing);
  }

  // ============================================
  // Backwards Compatibility Getters
  // (For existing consumers during migration)
  // ============================================

  /** @deprecated Use rawInput instead */
  @computed
  get url(): string {
    return this.urlEntries[0]?.url ?? '';
  }

  /** @deprecated Use setRawInput instead */
  @action
  setUrl(url: string) {
    this.setRawInput(url);
  }

  /** @deprecated Use urlEntries[0].unfurlData instead */
  @computed
  get unfurlData(): AnyDataPayload | null {
    return this.urlEntries[0]?.unfurlData ?? null;
  }

  /** @deprecated Use urlEntries[0].errorMessage instead */
  @computed
  get errorMessage(): string {
    return this.urlEntries[0]?.errorMessage ?? '';
  }

  /** @deprecated Use overallStatus instead */
  @computed
  get isLoading(): boolean {
    return this.urlEntries.some((e) => e.status === 'loading');
  }

  @computed
  get urlStatus(): URLStatus {
    const firstEntry = this.urlEntries[0];
    if (!firstEntry) return 'error';
    return firstEntry.status;
  }

  // ============================================
  // Shared Methods
  // ============================================

  @action
  setCollectionName(collectionName: string) {
    this.collectionName = collectionName;
  }

  @computed
  get suggestedLibraryName(): string {
    return this.collectionName === DEFAULT_COLLECTION_NAME
      ? DEFAULT_LIBRARY_NAME
      : this.collectionName;
  }

  @computed
  get urlIsDuplicate() {
    return false;
  }

  // TODO: Extend thumbnail getter to extract thumbnails for all content types (playlists, single videos,
  // Google Drive, LinkedIn, etc.), not just YouTube channels. Currently returns null for non-channel content.
  // See: https://github.com/ijw-fyi/web.front.v2/pull/550
  @computed
  get thumbnail(): string | null {
    const firstValid = this.validEntries[0];
    if (!firstValid?.unfurlData) return null;
    if (isYoutubeMultipleData(firstValid.unfurlData) && isYoutubeChannel(firstValid.unfurlData)) {
      const metadata = firstValid.unfurlData.data.metadata as Record<string, unknown> | undefined;
      const thumbnail = metadata?.thumbnail as string | undefined;
      if (thumbnail) return thumbnail;
      // Fallback to first video thumbnail
      const videos = convertUnfurlDataToUnfurlItem(firstValid.unfurlData, firstValid.url);
      return videos[0]?.thumbnail || null;
    }
    return null;
  }

  @computed
  get videoCount(): number {
    return this.totalVideoCount;
  }

  /**
   * True when the input is a Google Drive folder that contains no media (the
   * folder unfurled fine but has 0 importable files). Used to warn up front
   * rather than letting the user "add" an empty folder. Only fires when there's
   * no other media queued (videoCount 0), so it won't block a mixed selection.
   */
  @computed
  get isEmptyGoogleDriveFolder(): boolean {
    return (
      this.videoCount === 0 &&
      this.validEntries.some((e) => e.unfurlData && isGoogleDriveFolderData(e.unfurlData))
    );
  }

  /**
   * True when the input resolves to a SINGLE media item over the 10-hour upload
   * limit. Used to flag the URL pill as an error and disable the add button up
   * front (rather than rejecting only after the user clicks). Multi-item URLs
   * (playlists / Drive folders) route through the picker, which flags
   * over-limit items per row, so this intentionally covers only singles.
   */
  @computed
  get overDurationLimit(): boolean {
    const videos = this.allEnhancedVideos;
    if (videos.length !== 1) return false;
    const duration = videos[0]?.duration;
    return typeof duration === 'number' && duration > ERROR_DURATION_THRESHOLD;
  }

  @action
  setErrorMessage(errorMessage: string) {
    // For backwards compatibility, set on first entry if exists
    if (this.urlEntries[0]) {
      this.urlEntries[0].errorMessage = errorMessage;
    }
  }

  @action
  setAddingUrl(addingUrl: boolean) {
    this.isAddingUrl = addingUrl;
  }

  @action
  setLibraryName(libraryName: string | undefined) {
    this.libraryName = libraryName;
  }

  @computed
  get uploadButtonActive(): boolean {
    return this.validEntries.length > 0 && !this.isAddingUrl && !this.isLoading;
  }

  @action
  fetchUnfurlDataImmediately(url?: string) {
    // For multi-URL, fetch all entries immediately
    this.urlEntries.forEach((entry) => {
      if (entry.debounceTimeout) {
        clearTimeout(entry.debounceTimeout);
        entry.debounceTimeout = null;
      }
      if (entry.status === 'loading' || !entry.unfurlData) {
        this.fetchUnfurlDataForEntry(entry.id);
      }
    });
  }

  @action
  async uploadThisUrlToLibrary(
    libraryId: string,
    libraryName: string,
    isNewLibrary = false,
    uploadManager: UploadManager,
    folderPath: FolderPath = FOLDER_ROOT_PATH,
  ): Promise<UploadURLResult> {
    // For backwards compatibility, upload the first URL
    const firstEntry = this.urlEntries[0];
    if (!firstEntry) {
      return { success: false, error: 'No URL to upload' };
    }

    const result = await uploadManager.uploadURL(
      {
        url: firstEntry.url,
        library: {
          id: libraryId,
          name: libraryName,
        },
        unfurlData: firstEntry.unfurlData,
        isNewLibrary,
        folderPath,
      },
      this.userIntent,
    );

    if (result.success) {
      uploadManager.refreshLibrary(libraryId);
      this.setRawInput('');
    } else {
      this.setErrorMessage(result.error || 'Failed to upload URL');
    }
    return result;
  }

  @computed
  get isUrlAmbiguous(): boolean {
    const firstEntry = this.urlEntries[0];
    if (!firstEntry?.url) return false;
    if (firstEntry.status !== 'valid') return false;
    return Boolean(
      this.validationService.youtube.extractYouTubePlaylistId(firstEntry.url) &&
        Boolean(this.validationService.youtube.extractYouTubeVideoId(firstEntry.url)),
    );
  }

  @computed
  get collectionType(): 'playlist' | 'channel' | 'single' | 'ads_library' | null {
    const firstEntry = this.validEntries[0];
    if (!firstEntry?.unfurlData) return null;

    return getCollectionTypeFromUnfurlData(firstEntry.unfurlData);
  }

  @action
  async enhanceVideos() {
    // Enhance all entries
    await Promise.all(this.urlEntries.map((entry) => this.enhanceEntryVideos(entry.id)));
  }

  getNonDuplicateEstimate(existingUniqueIds: Record<string, string>): IngestEstimate {
    const allEnhanced = this.allEnhancedVideos;
    if (allEnhanced.length > 0) {
      return estimateIngest(filterOutDuplicates(allEnhanced, existingUniqueIds));
    }
    return this.ingestEstimateAllEntries;
  }

  getVideoCountBreakdown(existingUniqueIds: Record<string, string>): {
    found: number;
    inLibrary: number;
    new: number;
  } {
    const found = this.totalVideoCount;
    const allEnhanced = this.allEnhancedVideos;

    if (allEnhanced.length > 0) {
      const inLibrary = allEnhanced.filter((video) =>
        isMediaDuplicate(video, existingUniqueIds),
      ).length;
      const newCount = found - inLibrary;
      return { found, inLibrary, new: newCount };
    }

    return { found, inLibrary: 0, new: found };
  }

  /**
   * Get estimated credits cost for all URLs' non-duplicate media
   * @param existingUniqueIds - IDs of videos already in library to filter duplicates
   * @param creditsLeft - Current credits available (can be null)
   * @returns Object with cost, current, and left credits, or null if not calculable
   */
  getCreditsCostInfo(
    existingUniqueIds: Record<string, string>,
    creditsLeft: number | null,
  ): { cost: number; current: number; left: number } | null {
    // Check if we have any valid entries and are not enhancing
    if (this.validEntries.length === 0 || this.isAnyEnhancing) {
      return null;
    }

    const cost = this.getNonDuplicateEstimate(existingUniqueIds).credits;
    if (cost <= 0) {
      return null;
    }

    const current = creditsLeft || 0;

    return {
      cost,
      current,
      left: current - cost,
    };
  }

  /**
   * Get estimated storage cost for all URLs' non-duplicate media
   * @param existingUniqueIds - IDs of videos already in library to filter duplicates
   * @param storageLeft - Current storage available in seconds
   * @returns Object with cost, current, and left storage, or null if not calculable
   */
  getStorageCostInfo(
    existingUniqueIds: Record<string, string>,
    storageLeft: number,
  ): { cost: number; current: number; left: number } | null {
    // Check if we have any valid entries and are not enhancing
    if (this.validEntries.length === 0 || this.isAnyEnhancing) {
      return null;
    }

    const cost = this.getNonDuplicateEstimate(existingUniqueIds).durationSeconds;
    if (cost <= 0) {
      return null;
    }

    return {
      cost,
      current: storageLeft,
      left: storageLeft - cost,
    };
  }

  @action
  setUserIntent(intent: 'single' | 'playlist' | null) {
    this.userIntent = intent;
  }

  @computed
  get urlType(): 'single' | 'playlist' | 'channel' | 'ads_library' | null {
    if (!this.unfurlData) return null;
    return getCollectionTypeFromUnfurlData(this.unfurlData);
  }

  @action
  async handleUploadUrl(
    libraryManager: any,
    uploadManager: UploadManager,
    library?: { data: { name: string } | null; libraryId?: string },
    createNewLibrary: boolean = false,
    newLibraryName?: string,
    /** The folder of `library` to file the media in; a new library has only its root. */
    folderPath: FolderPath = FOLDER_ROOT_PATH,
  ): Promise<{ success: boolean; libraryId?: string; libraryName?: string }> {
    try {
      this.setAddingUrl(true);
      this.setErrorMessage('');

      trackingStore.urlPasted({
        libraryId: library?.libraryId ?? 'new',
        urlCount: this.validEntries.length,
      });

      if (createNewLibrary) {
        const library = await libraryManager.createNewLibrary(
          newLibraryName || this.suggestedLibraryName,
        );
        const result = await this.uploadThisUrlToLibrary(
          library._id,
          library.name,
          true,
          uploadManager,
        );
        return { success: result.success, libraryId: library._id, libraryName: library.name };
      } else {
        if (!library?.data) {
          this.setErrorMessage('No library selected');
          return { success: false };
        }
        const result = await this.uploadThisUrlToLibrary(
          library.libraryId!,
          library.data.name,
          false,
          uploadManager,
          folderPath,
        );
        return { success: result.success };
      }
    } catch (error) {
      this.setErrorMessage('An unexpected error occurred');
      return { success: false };
    } finally {
      this.setAddingUrl(false);
    }
  }

  // ============================================
  // Status Filter Actions (Phase 3)
  // ============================================

  @action
  toggleStatusFilter(type: MediaStatusType) {
    if (this.activeStatusFilters.has(type)) {
      this.activeStatusFilters.delete(type);
    } else {
      this.activeStatusFilters.add(type);
    }
    // MobX needs a new Set reference to trigger reactivity
    this.activeStatusFilters = new Set(this.activeStatusFilters);
  }

  @action
  clearStatusFilters() {
    this.activeStatusFilters = new Set();
  }

  @action
  setStatusDismissed(dismissed: boolean) {
    this.statusDismissed = dismissed;
  }

  override deinit() {
    // Detach from the cache and clear debounces — but deliberately do NOT cancel the scrapes.
    //
    // This runs when the Add media modal unmounts, e.g. on a backdrop click — which is exactly
    // when it used to abort everything and throw away minutes of channel scraping. The work now
    // lives in `unfurlCache` and survives. (Switching the destination library no longer gets
    // here at all: `UploadURLProvider` was hoisted above the keyed `LibraryFolderProvider`.)
    this.urlEntries.forEach((entry) => {
      entry.unsubscribeFromCache?.();
      if (entry.debounceTimeout) {
        clearTimeout(entry.debounceTimeout);
      }
    });

    super.deinit();
  }
}

export const { Provider: UploadURLProvider, useStore: useUploadURL } = createContext(
  UploadURLStore,
  {
    dispose: (store) => store.deinit(),
  },
);
